import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { useParams } from 'react-router-dom';

import {
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';

import {
    AesTypeEnum,
    getProjectSignatureSettings,
    openChangeCredentialsDialog,
    QesTypeEnum,
    saveProjectSignatureFile,
    saveProjectSignatureSettings,
    UserSignatureChangeTypeEnum,
} from '../../../redux/project/sliceProjectUserSignatures';
import RadioBtnGroup from '../Dialogs/_parts/RadioBtnGroup';
import { bodyFont, headingFontBold, vars } from '../../../Theme';
import Message from '../Dialogs/_parts/Message';
import { mdiAlertCircle } from '@mdi/js';

const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    flex: 1,
    '.MuiInput-root.MuiInputBase-root': {
        '&:after': {
            borderBottom: 'none',
            color: 'rgba(0, 0, 0, 0.42)',
        },

        '&:hover': {
            '&:before': {
                borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
            },
        },
    },
    'label.Mui-focused': {
        color: 'rgba(0, 0, 0, 0.6)',
    },
};

const passwordInputDiscSx = {
    '& .MuiInputBase-input': {
        WebkitTextSecurity: 'disc',
    },
};

const buttonSx: SxProps = {
    fontFamily: headingFontBold,
    textTransform: 'capitalize',
    backgroundColor: vars.brandGreen,
    color: vars.white,
    height: '32px',
    minWidth: '100px',
    ml: '1rem',
    fontSize: '12px',
    '&:hover': {
        backgroundColor: vars.selectedHoveredGreen,
    },
    '&:disabled': {
        color: vars.white,
        backgroundColor: vars.inactiveGreen,
    },
};

const initialMainState: ISignatureSettingsMainState = {
    aes: null,
    qes: null,
    signmeUsername: '',
    signmePAK: '',
    pfxFile: '',
};

const initialErrorState: ISignatureSettingsErrorState = {
    aes: false,
    qes: false,
    signmeUsername: false,
    signmePAK: false,
    pfxFile: false,
};

export const SignatureLabelErrorMessage = (text: string) => (
    <FormHelperText sx={{ marginLeft: '0px' }}>
        <Message
            text={text}
            icon={mdiAlertCircle}
            iconSize={0.75}
            color={vars.errorDialogRed}
            typographySx={{
                fontWeight: 400,
                fontSize: '12px',
                ...bodyFont,
            }}
            wrapperSx={{
                border: 'none',
                p: 0,
                m: 0,
                gap: 0.5,
            }}
        />
    </FormHelperText>
);

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'signatureSettings' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const params = useParams();
    const { replicaId } = params;

    const [mainState, setMainState] = useState<ISignatureSettingsMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<ISignatureSettingsErrorState>(initialErrorState);

    const { signature, loading: isLoading } = useSelector(
        (state: IAppReduxStore) => state.projectUserSignatureSettings,
    );

    const aesOptions = Object.values(AesTypeEnum).map(value => ({
        key: value,
        label: t(value),
    }));

    const qesOptions = Object.values(QesTypeEnum).map(value => ({
        key: value,
        label: t(value),
    }));

    useEffect(() => {
        if (replicaId) {
            dispatch(getProjectSignatureSettings({ replicaId }));
        }
    }, []);

    useEffect(() => {
        if (replicaId && signature) {
            setMainState(signature);
            if (!signature.aes) {
                setShowErrorState({ ...initialErrorState, aes: true });
            }
            if (!signature.qes) {
                setShowErrorState({ ...initialErrorState, qes: true });
            }
        }
    }, [signature]);

    const handleChange = ({
        field,
        value,
    }: {
        field: keyof ISignatureSettingsMainState;
        value: string | AesTypeEnum | QesTypeEnum;
    }) => {
        dispatch(
            saveProjectSignatureSettings({
                properties: {
                    [field]: value,
                },
                replicaId,
                typeOfChange: UserSignatureChangeTypeEnum.MainSettings,
            }),
        );
    };

    const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(
            saveProjectSignatureFile({
                replicaId,
                file: e.target.files[0],
            }),
        );
    };
    return (
        <Stack sx={{ width: '100%' }}>
            <FormControl size='small'>
                <FormLabel sx={formLabelSx}>{t('labelAes')}</FormLabel>
                <RadioBtnGroup
                    disabled={isLoading}
                    value={mainState.aes}
                    onSelect={key => handleChange({ field: 'aes', value: key })}
                    options={aesOptions}
                    containerSx={{
                        m: 0,
                        marginBottom: showErrorState.aes ? '0px' : '16px',
                    }}
                    groupSx={{
                        border: showErrorState.aes
                            ? `1px solid ${vars.errorDialogRed}`
                            : `1px solid ${vars.lightGray}`,
                    }}
                    border={true}
                />
                {showErrorState.aes && SignatureLabelErrorMessage(t('errorInputAes'))}
            </FormControl>
            <FormControl size='small'>
                <FormLabel sx={formLabelSx}>{t('labelQes')}</FormLabel>
                <RadioBtnGroup
                    disabled={isLoading}
                    value={mainState.qes}
                    onSelect={key => handleChange({ field: 'qes', value: key })}
                    options={qesOptions}
                    containerSx={{
                        m: 0,
                        marginBottom: showErrorState.qes ? '0px' : '16px',
                    }}
                    groupSx={{
                        border: showErrorState.qes
                            ? `1px solid ${vars.errorDialogRed}`
                            : `1px solid ${vars.lightGray}`,
                    }}
                    border={true}
                />
                {showErrorState.aes && SignatureLabelErrorMessage(t('errorInputQes'))}
            </FormControl>
            {(mainState.aes === AesTypeEnum.Pfx || mainState.qes === QesTypeEnum.Pfx) && (
                <FormControl size='small'>
                    <FormLabel sx={formLabelSx}>{t('labelSelectFile')}</FormLabel>
                    <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
                        <TextField
                            id={`selectFile`}
                            label={t('certificateFile')}
                            defaultValue={mainState.pfxFile}
                            variant='standard'
                            fullWidth
                            multiline={true}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                },
                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            sx={textFieldSx}
                        />
                        <Button
                            disabled={isLoading}
                            component='label'
                            variant='outlined'
                            sx={buttonSx}
                        >
                            {t('selectFile')}
                            <input hidden accept='.pfx' type='file' onChange={handleChangeFile} />
                        </Button>
                    </Stack>
                </FormControl>
            )}
            {(mainState.aes === AesTypeEnum.Signme || mainState.qes === QesTypeEnum.Signme) && (
                <FormControl size='small'>
                    <FormLabel sx={formLabelSx}>{t('labelRemoteSignature')}</FormLabel>
                    <Stack direction='row' spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
                        <Stack spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
                            <TextField
                                id={`usernameSignMe`}
                                label={t('signmeLabel')}
                                defaultValue={mainState.signmeUsername}
                                type='password'
                                variant='standard'
                                fullWidth
                                multiline={true}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                    inputLabel: {
                                        shrink: true,
                                    },
                                }}
                                sx={{ ...textFieldSx, ...passwordInputDiscSx }}
                            />
                            <TextField
                                id={`PAKSignMe`}
                                label={t('cardLabel')}
                                defaultValue={mainState.signmePAK}
                                type='password'
                                variant='standard'
                                fullWidth
                                multiline={true}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                    inputLabel: {
                                        shrink: true,
                                    },
                                }}
                                sx={{ ...textFieldSx, ...passwordInputDiscSx }}
                            />
                        </Stack>
                        <Button
                            component='label'
                            variant='outlined'
                            sx={buttonSx}
                            disabled={isLoading}
                            onClick={() => {
                                dispatch(openChangeCredentialsDialog());
                            }}
                        >
                            {t('changeSignMe')}
                        </Button>
                    </Stack>
                </FormControl>
            )}
        </Stack>
    );
};

export interface ISignatureSettingsMainState {
    aes: AesTypeEnum;
    qes: QesTypeEnum;
    signmeUsername: string;
    signmePAK: string;
    pfxFile: string;
}

export interface ISignatureSettingsErrorState {
    aes: boolean;
    qes: boolean;
    signmeUsername: boolean;
    signmePAK: boolean;
    pfxFile: boolean;
}
